import { memo, type ReactNode } from 'react';
import { cn } from '@/shared/lib/utils';
import { dateOfDay } from '../model/days';
import type { CalendarRowModel } from '../model/rowLayout';
import { TermBar } from './TermBar';

interface CalendarRowProps {
  row: CalendarRowModel;
  /** Zkratky dnů v týdnu (PO…NE) — na souvislé ose stojí u čísla dne. */
  weekdayLabels: readonly string[];
  highlightedTermId: string | null;
  onOpenTerm: (termId: string) => void;
  /** Akce nabízená na dni (zakládání termínů) — vykreslí se až na hover buňky. */
  renderDayAction?: (isoDay: string) => ReactNode;
  /** Ref na podkladovou mřížku dnů — z ní se měří šířka dne. */
  gridRef?: (element: HTMLDivElement | null) => void;
}

function isoDay(day: number): string {
  const date = dateOfDay(day);
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(date.getUTCDate()).padStart(2, '0');
  return `${date.getUTCFullYear()}-${month}-${dayOfMonth}`;
}

/**
 * Jedna řada dnů — týden (7 sloupců přes celou plochu), nebo výsek souvislé osy.
 *
 * Skládá se ze tří vrstev nad sebou: podkladová mřížka dnů (z ní se měří šířka dne), pruh s čísly
 * dnů (na ose sticky, aby zůstal viditelný při vodorovném posuvu) a plocha pruhů termínů.
 * Souřadnice si spočítal `layoutRow`; tady se jen vykreslují.
 *
 * Je **memoizovaná**. Když okno doroste o pár měsíců, model nezměněných řádků si podrží identitu
 * (viz `CalendarLayoutCache`), takže se překreslí jen ty nové — místo všech stovek.
 */
function CalendarRowImpl({
  row,
  weekdayLabels,
  highlightedTermId,
  onOpenTerm,
  renderDayAction,
  gridRef,
}: CalendarRowProps) {
  const stripTop = row.isAxis ? 21 : 0;

  return (
    <div
      className="border-phx-border relative border-b"
      style={{ minHeight: row.height, width: row.width }}
      data-testid="calendar-row"
    >
      <div
        ref={gridRef}
        aria-hidden="true"
        className="absolute inset-x-0 bottom-0 grid"
        style={{ top: stripTop, gridTemplateColumns: row.gridTemplateColumns }}
      >
        {row.cells.map((cell) => (
          <div key={cell.key} className="border-phx-border bg-phx-surface box-border border-r" />
        ))}
      </div>

      {row.monthSpans && (
        <div className="bg-phx-surface-3 border-phx-border-2 sticky top-0 z-[4] flex h-[21px] border-b">
          {row.monthSpans.map((span) => (
            <div
              key={span.key}
              className="border-phx-border-3 box-border flex min-w-0 shrink-0 items-center border-r"
              style={{ width: span.width }}
            >
              <span className="text-phx-text bg-phx-surface-3 text-phx-xs tracking-phx-wide sticky left-0 max-w-full overflow-hidden px-[9px] font-semibold text-ellipsis whitespace-nowrap uppercase">
                {span.label}
              </span>
            </div>
          ))}
        </div>
      )}

      <div
        className={cn(
          'bg-phx-surface-2 border-phx-border z-[3] grid h-[22px] border-b',
          row.isAxis ? 'sticky' : 'relative'
        )}
        style={{ top: stripTop, gridTemplateColumns: row.gridTemplateColumns }}
      >
        {row.cells.map((cell) => (
          <div
            key={cell.key}
            data-testid="calendar-day-cell"
            data-date={isoDay(cell.day)}
            className="group/day border-phx-border pr-phx-3 pl-phx-3 box-border flex items-center gap-[5px] border-r pt-[3px]"
          >
            {row.isAxis && (
              <span className="text-phx-text-2 tracking-phx-wide text-[9.5px] font-semibold uppercase">
                {weekdayLabels[cell.weekday]}
              </span>
            )}
            {cell.isToday ? (
              <span className="bg-phx-primary text-phx-primary-fg text-phx-xs inline-flex h-[17px] min-w-[19px] items-center justify-center rounded-full px-[5px] font-semibold tabular-nums">
                {cell.label}
              </span>
            ) : (
              <span className="text-phx-text-2 text-phx-xs tabular-nums">{cell.label}</span>
            )}
            {renderDayAction && (
              <span className="ml-auto opacity-0 transition-opacity group-hover/day:opacity-100">
                {renderDayAction(isoDay(cell.day))}
              </span>
            )}
          </div>
        ))}
      </div>

      <div className="mt-phx-3 relative" style={{ height: row.barsHeight }}>
        {row.bars.map((bar) => (
          <TermBar
            key={bar.key}
            bar={bar}
            isHighlighted={highlightedTermId === bar.termId}
            onOpen={onOpenTerm}
          />
        ))}
      </div>
    </div>
  );
}

export const CalendarRow = memo(CalendarRowImpl);
